import React, { useState } from 'react';
import { ConversionFunnel, FunnelStep } from '@/models/website-analytics.model';
import { WebsiteAnalyticsService } from '@/services/website-analytics.service';

interface FunnelFormProps {
  funnel?: ConversionFunnel | null;
  onSave: () => void;
  onCancel: () => void;
}

export const FunnelForm: React.FC<FunnelFormProps> = ({ funnel, onSave, onCancel }) => {
  const [name, setName] = useState(funnel?.name || '');
  const [description, setDescription] = useState(funnel?.description || '');
  const [steps, setSteps] = useState<FunnelStep[]>(
    funnel?.steps || [
      { id: '1', name: 'Landing Page', order: 1, isConversion: false },
    ]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addStep = () => {
    const newStep: FunnelStep = {
      id: Date.now().toString(),
      name: '',
      order: steps.length + 1,
      isConversion: false,
    };
    setSteps([...steps, newStep]);
  };

  const removeStep = (index: number) => {
    const newSteps = steps.filter((_, i) => i !== index).map((step, i) => ({
      ...step,
      order: i + 1,
    }));
    setSteps(newSteps);
  };

  const updateStep = (index: number, updates: Partial<FunnelStep>) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], ...updates };
    setSteps(newSteps);
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === steps.length - 1)
    ) {
      return;
    }

    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    
    // Update order numbers
    newSteps.forEach((step, i) => {
      step.order = i + 1;
    });

    setSteps(newSteps);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Funnel name is required');
      return;
    }

    if (steps.length === 0) {
      setError('At least one step is required');
      return;
    }

    if (steps.some(step => !step.name.trim())) {
      setError('All steps must have a name');
      return;
    }

    // Ensure at least one step is marked as conversion
    if (!steps.some(step => step.isConversion)) {
      setError('At least one step must be marked as a conversion');
      return;
    }

    setSaving(true);
    try {
      const funnelData: Partial<ConversionFunnel> = {
        name: name.trim(),
        description: description.trim() || undefined,
        steps: steps.map(step => ({
          ...step,
          name: step.name.trim(),
        })),
        isActive: funnel?.isActive ?? true,
      };

      if (funnel?.id) {
        await WebsiteAnalyticsService.updateFunnel(funnel.id, funnelData);
      } else {
        await WebsiteAnalyticsService.createFunnel(funnelData);
      }

      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to save funnel');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {funnel ? 'Edit Funnel' : 'Create Conversion Funnel'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Define the steps visitors take from initial contact to conversion
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Basic Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Funnel Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="e.g., Employer Sign-up Funnel"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Describe the purpose of this funnel"
            />
          </div>

          {/* Funnel Steps */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Funnel Steps <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={addStep}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add Step
              </button>
            </div>

            <div className="space-y-4">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium text-gray-500">
                          Step {step.order}
                        </span>
                        <input
                          type="text"
                          value={step.name}
                          onChange={(e) =>
                            updateStep(index, { name: e.target.value })
                          }
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          placeholder="Step name (e.g., Landing Page, Sign Up Form)"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Page URL (optional)
                          </label>
                          <input
                            type="text"
                            value={step.pageUrl || ''}
                            onChange={(e) =>
                              updateStep(index, { pageUrl: e.target.value || undefined })
                            }
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                            placeholder="/employers or /signup"
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Event Name (optional)
                          </label>
                          <input
                            type="text"
                            value={step.eventName || ''}
                            onChange={(e) =>
                              updateStep(index, { eventName: e.target.value || undefined })
                            }
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                            placeholder="form_submit, button_click"
                          />
                        </div>
                      </div>

                      <div className="mt-3 flex items-center">
                        <input
                          type="checkbox"
                          id={`conversion-${step.id}`}
                          checked={step.isConversion || false}
                          onChange={(e) =>
                            updateStep(index, { isConversion: e.target.checked })
                          }
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`conversion-${step.id}`}
                          className="ml-2 text-sm text-gray-700"
                        >
                          Mark as conversion step
                        </label>
                      </div>
                    </div>

                    <div className="flex flex-col space-y-1 ml-4">
                      <button
                        type="button"
                        onClick={() => moveStep(index, 'up')}
                        disabled={index === 0}
                        className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveStep(index, 'down')}
                        disabled={index === steps.length - 1}
                        className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        ↓
                      </button>
                      {steps.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeStep(index)}
                          className="px-2 py-1 text-xs text-red-600 hover:text-red-800"
                          title="Remove step"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving}
            >
              {saving ? 'Saving...' : funnel ? 'Update Funnel' : 'Create Funnel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

